import React from 'react'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import { ActionButton } from '@emerald-works-nova/components'
import { CircularProgress, Grid, Tabs, Tab, useTheme, Box, Typography, Skeleton, Divider, Tooltip, IconButton, Chip } from '@mui/material'
import { BannerStatic, Panel, TabPanel } from '../../components'
import makeStyles from '@mui/styles/makeStyles'
import { PictureInPictureRounded, BuildCircleRounded, EmailRounded, PreviewRounded, ArrowBackRounded } from '@mui/icons-material'
import { SectionMap } from '../../reducers/skill-bite-edit/slice'
import { routes } from '../../routes'
import Details from './details'
import Build from './build'
import Email from './email'
import Preview from './preview'
import { SkillBiteEditSlice } from '../../reducers'
import { useConfirm } from 'material-ui-confirm'
import SkillBiteConfirmEditMode from './components/confirm-edit-mode'
import { multiNoWrap } from '../../util/multi-no-wrap'
import SkillBitePublishModal from './components/confirm-publish-modal'
import { skillBitePublishSchema } from '../../util/skill-bite-schemas'
import { TooltipHelperText } from './preview/components/publish-tooltip'

const sectionCompMap = {
  details: <Details />,
  build: <Build />,
  email: <Email />,
  preview: <Preview />
}

export const IconMap = {
  details: <PictureInPictureRounded />,
  build: <BuildCircleRounded />,
  email: <EmailRounded />,
  preview: <PreviewRounded />
}

const useStyles = makeStyles(({ spacing }) => ({
  controlBtnContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  sbName: {
    ...multiNoWrap(1),
    fontSize: '18px',
    fontWeight: '600'
  },
  btnDivider: {
    height: '45%',
    backgroundColor: 'rgba(0,0,0,0.38)',
    width: 2,
    margin: '0 2.5%',
    alignSelf: 'center'
  },
  tabs: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
  },
  tabWrapper: {
    flexDirection: 'row',
    textTransform: 'none',
    '& svg': {
      marginRight: spacing(1.5)
    }
  }
}))

export const TabChangeGuardContext = React.createContext(null)

const SkillBiteEdit = () => {
  const classes = useStyles()
  const { palette } = useTheme()
  const { skillBiteId } = useParams()
  const tabChangeGuard = React.useRef()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [publishModalOpen, setPublishModalOpen] = React.useState(false)
  const confirm = useConfirm()
  const [confirmEditModalOpen, setConfirmEditModalOpen] = React.useState(false)
  const editMode = useSelector(SkillBiteEditSlice.selectors.selectEditMode)
  const isPublished = useSelector(SkillBiteEditSlice.selectors.selectIsPublished)

  const skillBite = useSelector(SkillBiteEditSlice.selectors.selectSkillBite)
  const isSkillBiteValid = skillBitePublishSchema.isValidSync(skillBite)

  const [tabValue, setTabValue] = React.useState(Object.entries(SectionMap)[0][0])

  const [getSkillBite, deleteSkillBite] = useEvent([
    SkillBiteEditSlice.eventBus.getSkillBite({
      onError: (e) => {
        console.error('getSkillBite', e)
        BannerStatic.show({ label: 'Error trying to get skill bite', isError: true })
        navigate(routes.skillBiteList.path)
      }
    }),
    SkillBiteEditSlice.eventBus.deleteSkillBite({
      onSuccess: () => navigate(routes.skillBiteList.path)
    })
  ])

  useEventsOnViewLoad(() => {
    getSkillBite.trigger({ skillBiteId })
    return () => {
      dispatch(SkillBiteEditSlice.actions.reset())
    }
  }, [getSkillBite, skillBiteId])

  const handleBackToList = () => {
    navigate(routes.skillBiteList.path)
  }

  const handleTabChange = (selectedTab) => {
    if (tabChangeGuard.current) {
      tabChangeGuard.current(() => setTabValue(selectedTab))
      return
    }
    setTabValue(selectedTab)
  }

  const handleToggleEdit = () => {
    if (!editMode) {
      setConfirmEditModalOpen(true)
    } else {
      dispatch(SkillBiteEditSlice.actions.changeEditMode(!editMode))
    }
  }

  const handleDeleteSkillBite = () => {
    confirm({
      title: 'Delete skill bite',
      description: 'This is a destructive action. Are you sure you want to delete this skill bite?',
      confirmationText: 'Yes, delete it.',
      confirmationButtonProps: {
        id: 'delete-skill-bite-confirmation-button',
        'data-test': 'delete-skill-bite-confirmation-button'
      },
      cancellationText: 'No, thanks.',
      cancellationButtonProps: {
        id: 'cancel-delete-skill-bite-confirmation-button',
        'data-test': 'cancel-delete-skill-bite-confirmation-button'
      }
    }).then(() => {
      deleteSkillBite.trigger({ skillBiteId })
    }).catch(() => console.log('Deletion cancelled'))
  }

  const publishedBadge = isPublished && (
    <Chip
      label='Published'
      color='success'
      size='small'
      sx={{ borderRadius: '4px', color: '#FFF' }}
    />
  )

  const Title = () => {
    if (getSkillBite.isWorking) {
      return <Skeleton width={130} />
    }

    const titleText = (
      <Typography
        data-test='edit-skill-bite-title'
        className={classes.sbName}
      >
        {skillBite?.name}
      </Typography>
    )

    const title = skillBite?.name?.length > 28
      ? (
        <Tooltip placement='top' title={skillBite?.name}>
          {titleText}
        </Tooltip>
      )
      : titleText

    return (
      <Box display='flex' flexDirection='row' alignItems='center'>
        {title}
      </Box>
    )
  }

  return (
    <TabChangeGuardContext.Provider
      value={{
        tabChangeGuard
      }}
    >
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Panel padding={0}>
              <Box display='flex' flexDirection='column'>
                <Box display='flex' flexDirection='row' alignItems='center' justifyContent='space-between' padding={1} pb={0} borderBottom='1px solid #CCC'>
                  <Box display='flex' flexDirection='row' alignItems='center' sx={{ width: '334px' }}>
                    <Tooltip title='Back to list' placement='top'>
                      <IconButton
                        data-test='edit-skill-bite-back-button'
                        disabled={getSkillBite.isWorking}
                        onClick={handleBackToList}
                        aria-label='back to list'
                        size='medium'
                        sx={{ mr: 1, height: '56px', p: 0, borderRadius: '4px' }}
                      >
                        <ArrowBackRounded fontSize='inherit' />
                      </IconButton>
                    </Tooltip>
                    <Box display='flex' flexDirection='column'>
                      <Box display='flex' flexDirection='row' alignItems='baseline' gap={1}>
                        <Typography variant='caption'>Skill Bites /</Typography>
                        {publishedBadge}
                      </Box>
                      <Title />
                    </Box>
                  </Box>
                  <Tabs
                    indicatorColor='primary'
                    textColor='primary'
                    value={tabValue}
                    onChange={(_, newValue) => handleTabChange(newValue)}
                    aria-label='Editor Tabs'
                    data-test='edit-skill-bite-tabs-wrapper'
                    classes={{
                      root: classes.tabs
                    }}
                  >
                    {Object.entries(SectionMap).map(([key, section]) => (
                      <Tab
                        id={`tab-${key}`}
                        aria-controls={`tabpanel-${key}`}
                        key={key}
                        value={key}
                        label={section.title}
                        icon={IconMap[key]}
                        disabled={getSkillBite.isWorking}
                        classes={{ wrapper: classes.tabWrapper }}
                        data-test={`edit-skill-bite-tab-${key}`}
                      />
                    ))}
                  </Tabs>
                  <Box display='flex' flexDirection='row' gap={1} justifyContent='flex-end' sx={{ width: '334px' }}>
                    {!isPublished && (
                      <>
                        <ActionButton
                          variant='secondary'
                          disabled={getSkillBite.isWorking}
                          dataTest='edit-skill-bite-delete-button'
                          style={{ color: palette.error.main }}
                          isWorking={deleteSkillBite.isWorking}
                          onClick={handleDeleteSkillBite}
                        >
                          {getSkillBite.isWorking ? <Skeleton width={50} /> : 'Delete'}
                        </ActionButton>
                        <Divider orientation='vertical' flexItem />
                      </>
                    )}
                    {!isPublished && (
                      <>
                        <Tooltip
                          placement='bottom'
                          title={!isSkillBiteValid && <TooltipHelperText />}
                          data-test='edit-skill-bite-publish-criteria-tooltip'
                        >
                          <span>
                            <ActionButton
                              variant='primary'
                              disabled={getSkillBite.isWorking || !isSkillBiteValid}
                              dataTest='edit-skill-bite-publish-button'
                              onClick={() => setPublishModalOpen(true)}
                            >
                              {getSkillBite.isWorking ? <Skeleton width={50} /> : 'Publish'}
                            </ActionButton>
                          </span>
                        </Tooltip>
                        <SkillBitePublishModal open={publishModalOpen} setOpen={setPublishModalOpen} />
                      </>
                    )}
                    {isPublished && (
                      <ActionButton
                        variant={!editMode ? 'primary' : 'secondary'}
                        dataTest='edit-skill-bite-edit-mode-button'
                        disabled={getSkillBite.isWorking}
                        onClick={handleToggleEdit}
                      >
                        {getSkillBite.isWorking ? <Skeleton width={50} /> : editMode ? 'Cancel Edit' : 'Edit'}
                      </ActionButton>
                    )}
                  </Box>
                </Box>
                <Box>
                  {Object.entries(SectionMap).map(([key]) => (
                    <TabPanel key={key} value={tabValue} index={key}>
                      {
                        getSkillBite.isWorking
                          ? (
                            <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' height={300}>
                              <CircularProgress size={60} color='primary' />
                            </Box>
                          )
                          : sectionCompMap[key]
                      }
                    </TabPanel>
                  ))}
                </Box>
              </Box>

            </Panel>
          </Grid>
        </Grid>
        <SkillBiteConfirmEditMode
          open={confirmEditModalOpen}
          setOpen={setConfirmEditModalOpen}
        />
      </div>
    </TabChangeGuardContext.Provider>
  )
}

export default SkillBiteEdit
